import React, { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FaArrowLeft, FaSearch, FaTruck, FaGasPump } from "react-icons/fa";
import { MdLocalShipping } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import UserDataHome from "../components/user-data-home";
import Footer from "../components/footer";
import { PEDIDOS_PRONTOS_MOCK } from "../mocks/pedidos-prontos-carregamento";

function formatDateBR(iso) {
  if (!iso) return "–";
  const [y, m, d] = String(iso).split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function formatVolume(n) {
  return `${Number(n || 0).toLocaleString("pt-BR")} L`;
}

function statusBadge(status) {
  if (status === "pronto") {
    return { label: "Pronto p/ carregar", colorScheme: "green" };
  }
  if (status === "aguardando") {
    return { label: "Aguardando liberação", colorScheme: "orange" };
  }
  return { label: status || "—", colorScheme: "gray" };
}

const OrdemCarregamentoPage = () => {
  const navigate = useNavigate();
  const [busca, setBusca] = useState("");

  const pedidos = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return PEDIDOS_PRONTOS_MOCK;
    return PEDIDOS_PRONTOS_MOCK.filter((p) => {
      const hay = [
        p.numero,
        p.cliente,
        p.codigoCliente,
        p.cidade,
        p.placaSugerida,
        ...(p.itens || []).map((i) => i.descricao),
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [busca]);

  const prontosCount = PEDIDOS_PRONTOS_MOCK.filter(
    (p) => p.status === "pronto",
  ).length;

  const abrirCarregamento = (pedido) => {
    navigate(`/ordem-carregamento/${pedido.id}`, { state: { pedido } });
  };

  return (
    <Flex direction="column" minH="100vh" bg="gray.50">
      <UserDataHome />
      <Box flex="1" py={{ base: 4, md: 6 }} px={{ base: 4, md: 6 }}>
        <Box maxW="1400px" mx="auto">
          <Button
            leftIcon={<FaArrowLeft />}
            variant="outline"
            colorScheme="blue"
            mb={6}
            onClick={() => navigate("/home")}
          >
            Voltar
          </Button>

          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "stretch", md: "flex-end" }}
            gap={4}
            mb={6}
          >
            <Box>
              <HStack spacing={3} mb={1}>
                <Icon as={MdLocalShipping} color="#007abe" boxSize={7} />
                <Heading size="lg" color="gray.800">
                  Ordem de carregamento
                </Heading>
              </HStack>
              <Text color="gray.600" fontSize="sm">
                Pedidos prontos para carregamento · {prontosCount} liberado
                {prontosCount === 1 ? "" : "s"} (dados mock)
              </Text>
            </Box>
            <InputGroup maxW={{ base: "100%", md: "320px" }}>
              <InputLeftElement pointerEvents="none">
                <Icon as={FaSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                bg="white"
                placeholder="Buscar pedido, cliente, placa..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            </InputGroup>
          </Flex>

          {pedidos.length === 0 ? (
            <Box
              bg="white"
              borderRadius="xl"
              border="1px solid"
              borderColor="gray.200"
              p={10}
              textAlign="center"
            >
              <Text color="gray.500">Nenhum pedido encontrado.</Text>
            </Box>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
              {pedidos.map((pedido) => {
                const badge = statusBadge(pedido.status);
                const podeCarregar = pedido.status === "pronto";
                return (
                  <Box
                    key={pedido.id}
                    as="button"
                    textAlign="left"
                    bg="white"
                    borderRadius="xl"
                    border="1px solid"
                    borderColor="gray.200"
                    p={5}
                    cursor={podeCarregar ? "pointer" : "not-allowed"}
                    opacity={podeCarregar ? 1 : 0.72}
                    transition="all 0.2s"
                    _hover={
                      podeCarregar
                        ? {
                            borderColor: "#007abe",
                            boxShadow: "md",
                            transform: "translateY(-2px)",
                          }
                        : undefined
                    }
                    onClick={() => {
                      if (podeCarregar) abrirCarregamento(pedido);
                    }}
                    disabled={!podeCarregar}
                  >
                    <Flex justify="space-between" align="flex-start" mb={3}>
                      <VStack align="start" spacing={0}>
                        <Text
                          fontSize="xs"
                          color="gray.500"
                          fontWeight="semibold"
                          textTransform="uppercase"
                          letterSpacing="wide"
                        >
                          Pedido #{pedido.numero}
                        </Text>
                        <Text fontWeight="bold" color="gray.800" fontSize="md">
                          {pedido.cliente}
                        </Text>
                      </VStack>
                      <Badge colorScheme={badge.colorScheme} borderRadius="md">
                        {badge.label}
                      </Badge>
                    </Flex>

                    <Text fontSize="sm" color="gray.600" mb={3}>
                      {pedido.codigoCliente}/{pedido.loja} · {pedido.cidade}
                    </Text>

                    <HStack spacing={4} mb={3} flexWrap="wrap">
                      <HStack spacing={1.5}>
                        <Icon as={FaGasPump} color="#007abe" boxSize={3.5} />
                        <Text fontSize="sm" fontWeight="medium" color="gray.700">
                          {formatVolume(pedido.volumeTotal)}
                        </Text>
                      </HStack>
                      <Text fontSize="sm" color="gray.500">
                        {formatDateBR(pedido.dataPedido)}
                      </Text>
                    </HStack>

                    <VStack align="stretch" spacing={1} mb={4}>
                      {(pedido.itens || []).map((item) => (
                        <Flex
                          key={`${pedido.id}-${item.codigo}`}
                          justify="space-between"
                          fontSize="sm"
                          color="gray.700"
                        >
                          <Text noOfLines={1}>{item.descricao}</Text>
                          <Text fontWeight="medium" color="gray.600" pl={2}>
                            {formatVolume(item.volume)}
                          </Text>
                        </Flex>
                      ))}
                    </VStack>

                    <HStack
                      spacing={2}
                      pt={3}
                      borderTop="1px solid"
                      borderColor="gray.100"
                      color="gray.600"
                      fontSize="sm"
                    >
                      <Icon as={FaTruck} color="#007abe" />
                      <Text>
                        {pedido.placaSugerida
                          ? `Frota sugerida: ${pedido.placaSugerida}`
                          : "Definir frota no carregamento"}
                      </Text>
                    </HStack>

                    {podeCarregar && (
                      <Text
                        mt={3}
                        fontSize="xs"
                        fontWeight="semibold"
                        color="#007abe"
                      >
                        Clique para iniciar o carregamento →
                      </Text>
                    )}
                  </Box>
                );
              })}
            </SimpleGrid>
          )}
        </Box>
      </Box>
      <Footer />
    </Flex>
  );
};

export default OrdemCarregamentoPage;
